import { Auction, AuctionBid } from '@/api';
import BidTimer from '@/components/general/BidTimer';
import Price from '@/components/general/Price';
import clsx from 'clsx';
import i18next from 'i18next';
import React, { useMemo } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
    CheckCircleIcon
} from '@heroicons/react/24/outline';
import ClockIcon from 'public/assets/icons/clock.svg';
import CloseIcon from 'public/assets/icons/close.svg';
import { motion } from 'framer-motion';
import ChevronIcon from 'public/assets/icons/chevron-down.svg';
import dateFormat from 'dateformat';
import Link from 'next/link';
import { Constants } from '@/helpers/Constants';

type Props = {
    hasAuction: boolean | undefined;
    isLoosing: boolean | undefined;
    lastBid: AuctionBid | undefined;
    showAuctionPhaseInfo: boolean;
    setShowAuctionPhaseInfo: (val: boolean) => void;
    isWinning: boolean | undefined;
    auction: Auction | undefined;
    endTime: Date | undefined;
    comingSoon: { startTime: Date } | undefined;
};

const PBBidStatus = ({ hasAuction, auction, isWinning, showAuctionPhaseInfo, isLoosing, lastBid, comingSoon, endTime, setShowAuctionPhaseInfo }: Props) => {
    const bidButtonTooltip = useMemo(() => {
        switch (auction?.status) {
            case Auction.StatusEnum.NotStarted:
                return (
                    <>{i18next.t('payment.bid-tooltip-coming-soon', {
                        startDate: dateFormat(auction?.start_date, 'mm/dd/yyyy'),
                        startTime: dateFormat(auction?.start_date, 'hh:MM tt'),
                        endDate: dateFormat(endTime, 'mm/dd/yyyy'),
                        endTime: dateFormat(endTime, 'hh:MM tt'),
                        interpolation: { escapeValue: false }
                    })}{' '}<Link href={Constants.Support.BIDDING_PROCESS} className="underline" target="_blank">{i18next.t('button-text.learn-more')}</Link>
                    </>
                );
            case Auction.StatusEnum.Open:
                return i18next.t('payment.bid-tooltip-open', { date: dateFormat(endTime, 'mm/dd/yyyy hh:MM:ss tt'), interpolation: { escapeValue: false } });
            case Auction.StatusEnum.Extended:
                return i18next.t('payment.bid-tooltip-extended', { date: dateFormat(endTime, 'mm/dd/yyyy hh:MM:ss tt'), interpolation: { escapeValue: false } });
            case Auction.StatusEnum.Blind:
                return i18next.t('payment.bid-tooltip-blind', { date: dateFormat(endTime, 'mm/dd/yyyy hh:MM:ss tt'), interpolation: { escapeValue: false } });
            case Auction.StatusEnum.Closed:
            case Auction.StatusEnum.Done:
                return i18next.t('payment.bid-tooltip-closed');
            default:
                return undefined;
        }
    }, [auction?.status, auction?.start_date, endTime]);

    if (auction?.type !== Auction.TypeEnum.Auction) {
        if (!comingSoon) {
            return null;
        }
        return (
            <div className="w-full">
                <span className="flex items-center text-[12px] font-[500] text-grey15">{i18next.t('item-text.sale-starts')}:</span>
                <div className="flex flex-row justify-between align-center w-full ">
                    <span className="text-[1.375rem] font-[700]"><BidTimer dueDate={comingSoon.startTime} /></span>
                    <span className="text-[14px] font-[500] text-grey15">{dateFormat(comingSoon.startTime, 'mm/dd/yyyy hh:MM:ss tt')}</span>
                </div>
            </div>
        );
    }
    let bidPhaseIcon = <CheckCircleIcon className="w-[20px] text-green4" />;
    let currentBidText: string = i18next.t('item-text.current-bid-text.current-bid');
    let bidTitle: string | undefined = i18next.t('item-text.current-bid-text.open');
    let currentPrice = <Price className={clsx('text-[1.375rem] font-[700]')} state={isWinning === true ? 'positive' : isWinning === false ? 'negative' : undefined} justification="L" number={auction?.current_amount ?? '0'} />;
    switch (auction?.status) {
        case Auction.StatusEnum.NotStarted:
            bidPhaseIcon = <ClockIcon className="w-[20px] text-grey15" />;
            bidTitle = i18next.t('item-text.coming-soon');
            currentBidText = i18next.t('item-text.open-bidding-starts');
            break;
        case Auction.StatusEnum.Extended:
            bidTitle = i18next.t('item-text.current-bid-text.extended-bidding');
            break;
        case Auction.StatusEnum.Blind:
            bidTitle = i18next.t('item-text.current-bid-text.blind-bidding');
            currentPrice = <div className={clsx('text-[1.375rem] font-[700]')}>{i18next.t('general.currency')} --{i18next.t('general.thousands-separator')}---.--</div>;
            break;
        case Auction.StatusEnum.Closed:
        case Auction.StatusEnum.Done:
            bidTitle = i18next.t('product-bid.closed');
            bidPhaseIcon = <CloseIcon className="w-[20px] text-grey15" />;
            if (isWinning === true) {
                currentBidText = i18next.t('item-text.current-bid-text.your-winning-bid');
            } else if (isWinning === false) {
                currentBidText = i18next.t('item-text.current-bid-text.winning-bid');
            } else {
                currentBidText = i18next.t('item-text.current-bid-text.closing-bid');
            }
            break;
        default:
            break;
    }
    return (
        <div>
            {bidTitle && (<div className="flex items-center text-[12px] font-[500] text-grey15">{i18next.t('item-text.auction-phase')}</div>)}
            <div className="flex space-x-[5px] items-center justify-between cursor-pointer" onClick={() => setShowAuctionPhaseInfo(!showAuctionPhaseInfo)}>
                <div className="w-full flex items-center select-none">
                    <span>{bidPhaseIcon}</span>
                    <span className="text-black text-[18px] font-[500] ml-[5px]">{bidTitle}</span>
                </div>

                <ReactTooltip
                    id="tooltip-explenation"
                    place="top"
                    content="Expand"
                />
                <div data-tooltip-id="tooltip-explenation">
                    <motion.span animate={{ transform: showAuctionPhaseInfo ? 'rotate(180deg)' : 'rotate(0deg)' }} className="w-[40px] h-[36px] hover:bg-grey14 rounded-full flex justify-center items-center">
                        <ChevronIcon />
                    </motion.span>
                </div>
            </div>
            <motion.div animate={{ height: showAuctionPhaseInfo ? 'fit-content' : 0 }} className="text-grey15 text-[14px] font-[500] mt-[10px] overflow-hidden h-0">
                {bidButtonTooltip}
            </motion.div>
            <div className="flex justify-between items-center border-t-[1px] pt-[20px] mt-[20px]">

                {hasAuction &&
                    <div>
                        <span className="flex items-center text-[12px] font-[500] text-grey15">{currentBidText}:</span>
                        <span>{currentPrice}</span>
                    </div>
                }

                {isLoosing === true && lastBid &&
                    <div>
                        <span className="flex items-center text-[12px] font-[500] text-grey15">{i18next.t('item-text.current-bid-text.your-bid')}:</span>
                        <Price className={clsx('text-[1.375rem] font-[700]')} state="negative" justification="L" number={lastBid?.max_amount || lastBid.amount} />
                    </div>
                }

                {isWinning && lastBid &&
                    <div>
                        <span className="flex items-center text-[12px] font-[500] text-grey15">{i18next.t('item-text.current-bid-text.your-bid')}</span>
                        <Price className={clsx('text-[1.375rem] font-[700]')} state="positive" justification="L" number={lastBid?.max_amount || lastBid.amount} />
                    </div>
                }

                {hasAuction && <span className="text-[14px] font-[500] text-grey15">{auction?.amount_of_bids} {i18next.t('item-text.current-bid-text.bids')}</span>}

                {comingSoon &&
                    <div className="w-full">
                        <span className="flex items-center text-[12px] font-[500] text-grey15">{currentBidText}:</span>
                        <div className="flex flex-row justify-between align-center w-full ">
                            <span className="text-[1.375rem] font-[700]"><BidTimer dueDate={comingSoon.startTime} /></span>
                            <span className="text-[14px] font-[500] text-grey15">{dateFormat(comingSoon.startTime, 'mm/dd/yyyy hh:MM:ss tt')}</span>
                        </div>
                    </div>
                }
            </div>
        </div>
    );
};

export default PBBidStatus;
